/**
 * Device-session status pipeline for the Technician Worklist (PROD-2007).
 *
 * The worklist lists today's device sessions (HBOT chamber, cryo bed, …) for
 * the person operating the machine, and offers exactly ONE button per row:
 * the next forward step.
 *
 *   Booked ─▶ Arrived ─▶ CheckedIn ─▶ Fulfilled
 *
 * FORWARD ONLY — there is deliberately no un-mark, no cancel and no no-show on
 * this surface. The technician holds no clinical licence, so nothing here may
 * reverse a session or end one early; cancel and no-show belong to the front
 * desk (Andrew, 16 Sep). Do not "complete the set" by adding the backward
 * transitions the left edit panel has (`status-button-window.ts`) — that is a
 * different surface with a different operator.
 *
 * NAMING: `Fulfilled` is the wire code; **"Completed" is the display label**.
 * That split is platform-wide (see `status-badge.tsx` — `fulfilled` renders as
 * "Completed" everywhere), not a bug in this file. `code` below is what goes
 * on the wire; `label` is what the button says. Never send "Completed".
 *
 * WHY THIS FILE DOES NOT USE `appointmentStatusFromCode`
 * ------------------------------------------------------
 * The obvious move is to put the raw status on the `AppointmentStatusType`
 * axis and key off that. Don't. That axis is a **display-colour** axis — it is
 * declared off `statusBadgeVariants`, and two of its buckets are documented in
 * `status-badge.tsx` as pure presentation decisions (`EnteredInError` folded
 * into `cancelled`; `Pending`/`Proposed` folded into `waitlisted`). Deriving a
 * write permission from it couples a real appointment mutation to how a pill
 * is coloured: re-bucket `waitlist` onto `booked` for cosmetic reasons — a
 * legitimate edit, reviewed by whoever owns badges — and this module would
 * start offering "Mark arrived" for a waitlisted patient.
 *
 * So the two maps below are keyed on the normalised status token directly.
 * That makes them whitelists BY CONSTRUCTION: an unrecognised status simply
 * misses both, yielding no step and no terminal claim, with no guard needed.
 * The only thing shared with `status-badge.tsx` is the normalisation rule.
 */

/** The three wire codes this surface is allowed to write. Nothing else. */
export type SessionStatusCode = 'Arrived' | 'CheckedIn' | 'Fulfilled';

/** One forward step a technician can take on a device session. */
export interface SessionStatusStep {
  /** The wire status code to send. */
  readonly code: SessionStatusCode;
  /** Button label — 'Mark arrived' | 'Mark checked in' | 'Mark completed'. */
  readonly label: string;
}

/**
 * Lower-case, strip spaces/hyphens/underscores. Same rule as
 * `appointmentStatusFromCode` in `status-badge.tsx`, so wire codes
 * (`CheckedIn`), display strings (`Checked In`) and casing drift all land on
 * one token. Deliberately duplicated rather than imported — see the header.
 */
function statusToken(raw: string | null | undefined): string {
  return (raw ?? '').toLowerCase().replace(/[\s_-]/g, '');
}

/**
 * The three statuses a technician can advance FROM, and where each goes.
 *
 * A `Map`, not an object literal: an object would resolve inherited keys, so
 * a status token of `constructor` or `toString` would return something
 * truthy-but-wrong instead of missing the map.
 */
const FORWARD_STEPS: ReadonlyMap<string, SessionStatusStep> = new Map([
  ['booked', { code: 'Arrived', label: 'Mark arrived' } as const],
  ['arrived', { code: 'CheckedIn', label: 'Mark checked in' } as const],
  ['checkedin', { code: 'Fulfilled', label: 'Mark completed' } as const],
]);

/**
 * Statuses a session cannot move on from, as tokens. Both spellings of
 * cancelled are listed because the wire has shipped each; `completed` is the
 * display twin of `fulfilled`.
 */
const TERMINAL_TOKENS: ReadonlySet<string> = new Set([
  'fulfilled',
  'completed',
  'noshow',
  'cancelled',
  'canceled',
  'enteredinerror',
]);

/**
 * The single forward step available from `statusCode`, or `null` when the
 * session is terminal, is not on the technician's pipeline at all (Proposed /
 * Pending / Waitlist / Confirmed), or the status is missing or unrecognised.
 *
 * Returns a shared frozen-by-convention object, so the reference is stable
 * across renders and safe in a `useMemo` dependency list.
 */
export function nextSessionStep(
  statusCode: string | null | undefined,
): SessionStatusStep | null {
  return FORWARD_STEPS.get(statusToken(statusCode)) ?? null;
}

/**
 * True when the status IS `Booked` — the first rung of the pipeline — as
 * against a status that merely LOOKS booked further downstream.
 *
 * The worklist needs the distinction because `status-badge.tsx` folds every
 * status it cannot read into its neutral `booked` bucket: on the colour axis
 * "Booked" and "unrecognised" are one value. Answered here because this is the
 * module that owns the normalisation rule, and asking the caller to retype
 * `toLowerCase().replace(...)` a third time is how the three copies would
 * start to disagree.
 */
export function isBookedSessionStatus(
  statusCode: string | null | undefined,
): boolean {
  return statusToken(statusCode) === 'booked';
}

/**
 * The two spellings of "this session ran to the end". `fulfilled` is the wire
 * code, `completed` its display twin — the same split `TERMINAL_TOKENS`
 * carries, kept as its own set because the question is narrower than
 * terminality: a NoShow and a Cancelled session are also over, but they never
 * happened, so there is no printout to file against them.
 */
const COMPLETED_TOKENS: ReadonlySet<string> = new Set(['fulfilled', 'completed']);

/**
 * True when the session RAN and finished — `Fulfilled` / `Completed` — as
 * against merely being over (`NoShow`, `Cancelled`, `EnteredInError`), which
 * `isTerminalSessionStatus` below answers.
 *
 * The worklist needs the narrower question for the report paperclip: a
 * technician who marks a session complete and only then walks back from the
 * machine with the printout must still be able to file it (Andrew, 18 Sep),
 * while a session that never ran has nothing to attach. Answered here for the
 * same reason as `isBookedSessionStatus` — this module owns the normalisation
 * rule, and a fourth hand-typed `toLowerCase().replace(...)` is how the copies
 * start to disagree.
 *
 * Note this says nothing about the pipeline: `nextSessionStep` is forward-only
 * and a completed session still has no step. This is about the paperclip.
 */
export function isCompletedSessionStatus(
  statusCode: string | null | undefined,
): boolean {
  return COMPLETED_TOKENS.has(statusToken(statusCode));
}

/**
 * True only when the session has definitively ended — `Fulfilled`, `NoShow`,
 * `Cancelled` or `EnteredInError`.
 *
 * An unknown status is **false**: it is unknown, not finished. The caller
 * renders a dash for both (no step is available either way), but must never be
 * told a session it cannot read has already ended.
 */
export function isTerminalSessionStatus(
  statusCode: string | null | undefined,
): boolean {
  return TERMINAL_TOKENS.has(statusToken(statusCode));
}
